import { useCallback, useEffect, useState } from 'react'
import { Message } from '@arco-design/web-react'
import { useTranslation } from 'react-i18next'
import {
  updateUserSettingApi,
  updateUserInfoApi,
  type NotifyType,
  type UpdateUserSettingParams,
} from '@/api/user'
import { normalizeLanguage } from '@/i18n/language'
import { parseUserSetting, type UserSettingParsed } from '@/utils/userSetting'

export interface NotificationFormValues {
  notify_type: NotifyType
  quota_warning_threshold: number
  webhook_url: string
  webhook_secret: string
  notification_email: string
  bark_url: string
  gotify_url: string
  gotify_token: string
  gotify_priority: number
  upstream_model_update_notify_enabled: boolean
  accept_unset_model_ratio_model: boolean
  record_ip_log: boolean
}

function toFormValues(setting: UserSettingParsed): NotificationFormValues {
  return {
    notify_type: setting.notify_type || 'email',
    quota_warning_threshold: setting.quota_warning_threshold ?? 500000,
    webhook_url: setting.webhook_url || '',
    webhook_secret: setting.webhook_secret || '',
    notification_email: setting.notification_email || '',
    bark_url: setting.bark_url || '',
    gotify_url: setting.gotify_url || '',
    gotify_token: setting.gotify_token || '',
    gotify_priority: setting.gotify_priority ?? 5,
    upstream_model_update_notify_enabled: !!setting.upstream_model_update_notify_enabled,
    accept_unset_model_ratio_model: !!setting.accept_unset_model_ratio_model,
    record_ip_log: !!setting.record_ip_log,
  }
}

function isValidUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

export function validateNotificationForm(values: NotificationFormValues): string | null {
  if (!values.quota_warning_threshold || values.quota_warning_threshold <= 0) {
    return 'personal.notify.thresholdInvalid'
  }
  if (values.notify_type === 'email' && values.notification_email && !values.notification_email.includes('@')) {
    return 'personal.notify.emailInvalid'
  }
  if (values.notify_type === 'webhook') {
    if (!values.webhook_url) return 'personal.notify.webhookRequired'
    if (!isValidUrl(values.webhook_url)) return 'personal.notify.webhookInvalid'
  }
  if (values.notify_type === 'bark') {
    if (!values.bark_url) return 'personal.notify.barkRequired'
    if (!isValidUrl(values.bark_url)) return 'personal.notify.barkInvalid'
  }
  if (values.notify_type === 'gotify') {
    if (!values.gotify_url) return 'personal.notify.gotifyUrlRequired'
    if (!values.gotify_token) return 'personal.notify.gotifyTokenRequired'
    if (!isValidUrl(values.gotify_url)) return 'personal.notify.gotifyUrlInvalid'
  }
  if (values.gotify_priority < 0 || values.gotify_priority > 10) {
    return 'personal.notify.gotifyPriorityInvalid'
  }
  return null
}

export function useNotificationSettings(
  rawSetting: string | undefined,
  onSaved?: () => Promise<void>
) {
  const { t } = useTranslation()
  const [values, setValues] = useState<NotificationFormValues>(() =>
    toFormValues(parseUserSetting(rawSetting))
  )
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setValues(toFormValues(parseUserSetting(rawSetting)))
  }, [rawSetting])

  const patch = useCallback((patchValues: Partial<NotificationFormValues>) => {
    setValues((prev) => ({ ...prev, ...patchValues }))
  }, [])

  const save = useCallback(async () => {
    const errorKey = validateNotificationForm(values)
    if (errorKey) {
      Message.error(t(errorKey))
      return false
    }

    const languageBefore = parseUserSetting(rawSetting).language
    const payload: UpdateUserSettingParams = {
      notify_type: values.notify_type,
      quota_warning_threshold: values.quota_warning_threshold,
      accept_unset_model_ratio_model: values.accept_unset_model_ratio_model,
      record_ip_log: values.record_ip_log,
    }
    if (values.notify_type === 'webhook') {
      payload.webhook_url = values.webhook_url
      if (values.webhook_secret) payload.webhook_secret = values.webhook_secret
    }
    if (values.notify_type === 'email' && values.notification_email) {
      payload.notification_email = values.notification_email
    }
    if (values.notify_type === 'bark') payload.bark_url = values.bark_url
    if (values.notify_type === 'gotify') {
      payload.gotify_url = values.gotify_url
      payload.gotify_token = values.gotify_token
      payload.gotify_priority = values.gotify_priority
    }
    payload.upstream_model_update_notify_enabled = values.upstream_model_update_notify_enabled

    setSaving(true)
    try {
      await updateUserSettingApi(payload)
      const preferred = normalizeLanguage(String(languageBefore || ''))
      if (preferred) {
        await updateUserInfoApi({ language: preferred })
      }
      Message.success(t('personal.notify.saveSuccess'))
      await onSaved?.()
      return true
    } catch (err: unknown) {
      Message.error(err instanceof Error ? err.message : t('personal.notify.saveFailed'))
      return false
    } finally {
      setSaving(false)
    }
  }, [values, rawSetting, onSaved, t])

  return { values, patch, save, saving }
}
